/**
 * Ist Anmeldung bzw. Registrierung HIER offen? — die einzige Stelle, die das
 * beantwortet.
 *
 * Zwei Schalter müssen beide „offen" sagen:
 *
 * - **Plattform** (`/api/public/site-config`): `login_enabled` /
 *   `registration_enabled`, gepflegt in der Verwaltung.
 * - **Land** (`/api/i18n/locale-config`): `showLogin` / `showRegister` des
 *   gewählten Landes. Das sind Compliance-Schalter — welche Märkte gesperrt sind,
 *   entscheidet `backend/app/compliance/market_release.py`.
 *
 * Offen ist nur, was eine erfolgreiche Antwort ausdrücklich als `true` meldet.
 * Solange eine der beiden Antworten fehlt, ist der Stand `laedt`; scheitert
 * eine, ist er `fehler` — und in beiden Fällen ist nichts offen. Bis 15.09.2026
 * rechnete die Anmeldeseite `showRegister !== false` gegen eine statische
 * Länderliste ohne dieses Feld, und die Site-Config trug `registration_enabled:
 * true` als Vorgabe: Eine gescheiterte oder langsame Antwort öffnete die
 * Registrierung, auch in gesperrten Märkten. `/register` und die „Kostenlos
 * starten"-Knöpfe fragten gar nicht.
 *
 * Wer einen Weg in die Registrierung baut (Link, Knopf, Seite), fragt
 * `registrierung` hier ab. Wächter: `backend/tests/test_anmeldefreigabe_canon.py`.
 */
import { useCallback, useState } from 'react'

import { useLanguage } from './useLanguage'
import { useSiteConfig } from './useSiteConfig'

export type FreigabeStand = 'laedt' | 'bereit' | 'fehler'

/** Warum die Registrierung zu ist — bestimmt den Text, den der Nutzer liest. */
export type Sperrgrund = 'plattform' | 'land' | null

export interface AnmeldeFreigabe {
  stand: FreigabeStand
  login: boolean
  registrierung: boolean
  /** Nur bei `stand === 'bereit'` und geschlossener Registrierung gesetzt. */
  registrierungSperrgrund: Sperrgrund
  /** Kürzel des Landes, dessen Schalter gelten (z. B. `br`). */
  land: string
  neuLaden: () => Promise<void>
}

/**
 * Führt diese Adresse zur Registrierung?
 *
 * Für Ziele, die nicht im Quelltext stehen, sondern als Daten kommen — der
 * CTA eines Banners zum Beispiel. Geprüft wird der Pfad der EIGENEN Anwendung,
 * mit und ohne Sprach-Präfix (`/register`, `/br/portugues/register?plan=…`);
 * eine fremde Adresse ist keine.
 */
export function fuehrtZurRegistrierung(url: string): boolean {
  if (!url) return false
  let pfad = url
  if (/^https?:\/\//i.test(url)) {
    let ziel: URL
    try {
      ziel = new URL(url)
    } catch {
      return false
    }
    if (ziel.origin !== window.location.origin) return false
    pfad = ziel.pathname
  }
  pfad = pfad.split(/[?#]/)[0].replace(/\/+$/, '')
  return /(^|\/)register$/.test(pfad)
}

export function useAnmeldeFreigabe(): AnmeldeFreigabe {
  const { anmeldeFreigabe, ladeLaenderNeu, country } = useLanguage()
  const { config, loading, error, refreshConfig } = useSiteConfig()
  const [laeuft, setLaeuft] = useState(false)

  const neuLaden = useCallback(async () => {
    setLaeuft(true)
    try {
      await Promise.all([refreshConfig(), ladeLaenderNeu()])
    } finally {
      setLaeuft(false)
    }
  }, [refreshConfig, ladeLaenderNeu])

  const plattformStand: FreigabeStand = error ? 'fehler' : loading ? 'laedt' : 'bereit'
  if (plattformStand === 'bereit'
    && (typeof config.login_enabled !== 'boolean' || typeof config.registration_enabled !== 'boolean')) {
    console.error('site-config liefert keine Anmelde-Schalter:', config)
  }
  const plattformGueltig = typeof config.login_enabled === 'boolean'
    && typeof config.registration_enabled === 'boolean'

  let stand: FreigabeStand
  if (laeuft) {
    stand = 'laedt'
  } else if (plattformStand === 'fehler' || anmeldeFreigabe.stand === 'fehler'
    || (plattformStand === 'bereit' && !plattformGueltig)) {
    stand = 'fehler'
  } else if (plattformStand === 'laedt' || anmeldeFreigabe.stand === 'laedt') {
    stand = 'laedt'
  } else {
    stand = 'bereit'
  }

  const bereit = stand === 'bereit'
  const plattformRegistrierung = config.registration_enabled === true
  const registrierung = bereit && plattformRegistrierung && anmeldeFreigabe.registrierung
  const login = bereit && config.login_enabled === true && anmeldeFreigabe.login

  let registrierungSperrgrund: Sperrgrund = null
  if (bereit && !registrierung) {
    registrierungSperrgrund = plattformRegistrierung ? 'land' : 'plattform'
  }

  return { stand, login, registrierung, registrierungSperrgrund, land: country, neuLaden }
}
